import { useContext } from "react";
import { useDelete, useList, useNavigation } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import { Button, Popconfirm, Space, Table, Typography, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { AppTitle } from "components";
import { s3Path } from "helpers";
import { EnvContext } from "providers";
import { MedicineContest, MedicineContestType } from "./types";

type MedicineContestYearPageProps = {
  contestType?: MedicineContestType;
  basePath?: string;
  title?: string;
  addButtonText?: string;
  entityName?: string;
  emptyText?: string;
  singleItemPerYear?: boolean;
};

export const MedicineContestYearPage = ({
  contestType = "CONTEST",
  basePath = "/medicine/contests",
  title,
  addButtonText,
  entityName,
  emptyText,
  singleItemPerYear = false,
}: MedicineContestYearPageProps) => {
  const { THUMBOR_URL } = useContext(EnvContext);
  const { push } = useNavigation();
  const params = useParams();
  const pageTitle =
    title || (contestType === "CONFERENCE" ? "Конференции" : "Конкурс");
  const buttonText =
    addButtonText ||
    (contestType === "CONFERENCE"
      ? "Добавить конференцию"
      : "Добавить конкурс");
  const itemName =
    entityName || (contestType === "CONFERENCE" ? "Конференция" : "Конкурс");
  const noDataText =
    emptyText ||
    (contestType === "CONFERENCE"
      ? "Конференции отсутствуют"
      : "Конкурсы отсутствуют");
  const year = Number(params.year);

  const { data, isLoading, refetch } = useList<MedicineContest>({
    resource: "medicine-contests",
    dataProviderName: "medicineContestsDataProvider",
    meta: {
      year,
      contestType,
    },
    queryOptions: {
      enabled: Boolean(year),
    },
  });
  const { mutate: deleteContest, isLoading: deletingContest } = useDelete();

  const contests = data?.data || [];
  const getLinkUrl = (value?: string) => {
    if (!value) {
      return "";
    }

    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    return s3Path({
      path: value,
      original: true,
      thumbor: THUMBOR_URL,
    });
  };

  const handleContestDelete = (contest: MedicineContest) => {
    deleteContest(
      {
        dataProviderName: "medicineContestsDataProvider",
        resource: "medicine-contests",
        id: contest.id,
        meta: {
          contestType,
        },
      },
      {
        onSuccess: () => {
          message.success(`${itemName} удален`);
          refetch();
        },
        onError: (error: any) => {
          message.error(
            error?.message || `Не удалось удалить ${itemName.toLowerCase()}`
          );
        },
      }
    );
  };

  return (
    <Show
      isLoading={isLoading}
      title={
        <AppTitle canGoBack>
          {pageTitle} {year}
        </AppTitle>
      }
      breadcrumb={false}
      headerButtons={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={singleItemPerYear && contests.length > 0}
          onClick={() => push(`${basePath}/${year}/create`)}
        >
          {buttonText}
        </Button>
      }
      footerButtons={null}
      goBack={false}
    >
      <Table<MedicineContest>
        dataSource={contests}
        rowKey="id"
        pagination={false}
        locale={{
          emptyText: <div style={{ margin: 56 }}>{noDataText}</div>,
        }}
      >
        <Table.Column<MedicineContest>
          dataIndex="title"
          key="title"
          title="Название"
        />
        <Table.Column<MedicineContest>
          width={260}
          dataIndex="presentationsUrl"
          key="presentationsUrl"
          title="Презентации"
          render={(value) =>
            value ? (
              <Typography.Link href={value} target="_blank" rel="noreferrer">
                {value}
              </Typography.Link>
            ) : (
              "-"
            )
          }
        />
        {contestType === "CONFERENCE" && (
          <Table.Column<MedicineContest>
            width={260}
            dataIndex="recordingUrl"
            key="recordingUrl"
            title="Запись"
            render={(value) =>
              value ? (
                <Typography.Link
                  href={getLinkUrl(value)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {value}
                </Typography.Link>
              ) : (
                "-"
              )
            }
          />
        )}
        <Table.Column<MedicineContest>
          width={130}
          key="regulations"
          title="Документы"
          render={(_, record) => record.regulations?.length || 0}
        />
        <Table.Column<MedicineContest>
          width={220}
          fixed="right"
          key="actions"
          title="Действия"
          render={(_, record) => (
            <Space size={8} direction="vertical" style={{ width: "100%" }}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                  push(`${basePath}/${record.year}/edit/${record.id}`)
                }
                block
              >
                Редактировать
              </Button>
              <Popconfirm
                title={`Удалить ${itemName.toLowerCase()}?`}
                okText="Удалить"
                cancelText="Отмена"
                onConfirm={() => handleContestDelete(record)}
              >
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={deletingContest}
                  block
                >
                  Удалить
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>
    </Show>
  );
};
